<template>
  <Popover
    ref="popover_ref"
    @hide="emit('close')"
  >
    <div class="p-2">
      <div class="flex flex-col gap-2">
        <div class="flex flex-row items-center gap-2">
          <button
            :title="$t(`editor_ziggu.labels.${remove_label}`)"
            class="relative flex h-6 w-6 items-center justify-center rounded-full border transition-all duration-150 hover:scale-110 hover:border-gray-400 hover:shadow-md active:scale-95"
            style="box-shadow: 0 0 0 1px #d1d5db"
            @click="handleRemove"
          >
            <FontAwesomeIcon
              :icon="['far', 'xmark']"
              class="text-gray-400"
            />
          </button>

          <div class="h-8 w-px bg-gray-200" />

          <button
            v-for="color in colors"
            :key="color.value"
            :title="color.name"
            :style="{ backgroundColor: color.value }"
            :class="{ 'ring-2 ring-blue-300 ring-offset-1': color_active === color.value }"
            class="relative h-6 w-6 rounded-full border border-black/30 transition-all duration-150 hover:scale-110 active:scale-95"
            @click="handleColorSelect(color.value)"
          >
            <span
              v-if="color_active === color.value"
              class="absolute inset-0 flex items-center justify-center"
              :class="checkmark_color"
            >
              <FontAwesomeIcon :icon="['far', 'check']" />
            </span>
          </button>
        </div>
      </div>
    </div>
  </Popover>
</template>

<script setup>
import { computed, ref } from 'vue';
import Popover from 'primevue/popover';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  editor: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: true,
    validator: (value) => ['highlight', 'textColor'].includes(value),
  },
});

const emit = defineEmits(['select', 'remove', 'close']);

const popover_ref = ref(null);

const color_configs = {
  highlight: {
    colors: [
      { name: 'yellow', value: '#fbe604' },
      { name: 'green', value: '#c7fad8' },
      { name: 'blue', value: '#ceeafd' },
      { name: 'pink', value: '#e188b345' },
      { name: 'orange', value: '#e07c3945' },
      { name: 'purple', value: '#e4ccff' },
      { name: 'red', value: '#ffccd0' },
    ],
    remove_label: 'remove_highlight',
    checkmark_color: 'text-blue-600',
  },
  textColor: {
    colors: [
      { name: 'Black', value: '#000000' },
      { name: 'Dark Gray', value: '#374151' },
      { name: 'Gray', value: '#6B7280' },
      { name: 'Red', value: '#DC2626' },
      { name: 'Dark Red', value: '#991B1B' },
      { name: 'Orange', value: '#EA580C' },
      { name: 'Amber', value: '#D97706' },
      { name: 'Yellow', value: '#fbe604' },
      { name: 'Green', value: '#16A34A' },
      { name: 'Dark Green', value: '#15803D' },
      { name: 'Teal', value: '#0D9488' },
      { name: 'Blue', value: '#2563EB' },
      { name: 'Dark Blue', value: '#1E40AF' },
      { name: 'Indigo', value: '#4F46E5' },
      { name: 'Purple', value: '#9333EA' },
      { name: 'Pink', value: '#DB2777' },
      { name: 'Brown', value: '#92400E' },
    ],
    remove_label: 'remove_text_color',
    checkmark_color: 'text-white',
  },
};

const colors = computed(() => color_configs[props.type].colors);
const remove_label = computed(() => color_configs[props.type].remove_label);
const checkmark_color = computed(() => color_configs[props.type].checkmark_color);

const color_active = computed(() => {
  if (props.type === 'highlight') {
    const color = colors.value.find((c) => props.editor.isActive('highlight', { color: c.value }));
    return color ? color.value : null;
  }
  const color_found = colors.value.find((c) => {
    const attrs = props.editor.getAttributes('textStyle');
    return attrs.color === c.value;
  });
  return color_found ? color_found.value : null;
});

const handleColorSelect = (color) => {
  emit('select', color);
  popover_ref.value.hide();
};

const handleRemove = () => {
  emit('remove');
  popover_ref.value.hide();
};

defineExpose({
  toggle: (event) => {
    if (event && popover_ref.value) {
      popover_ref.value.toggle(event);
    }
  },
  hide: () => popover_ref.value.hide(),
});
</script>
